import { RACE_CORE_PRESET } from '../../../gameModes/race/preset'
import { specialTileDefinition } from '../../specialTiles/challengeSpecialTileParts'
import {
  createBoardDistributionStep,
  createBonusStep,
  createGridSizeStep,
  createIntervalTimeStep,
  createMinWordLengthStep,
  createSpecialTileStep,
  createVowelRatioBandStep,
} from '../steps'
import type { BoggleChallengeConfigurationDefinition } from '../types'

export const basic = {
  gameModeIds: ['race'],
  id: 'basic',
  weight: 10,
  metadata: {
    description: 'Basic challenge',
    name: 'Basic',
    shortLabel: 'Basic',
  },
  pathMode: 'free',
  resolveScoreThresholds: input => {
    let targetScore = 35

    // Grid size
    if (input.settings.gridSize === '3x3') {
      targetScore -= 2
    }
    if (input.settings.gridSize === '4x4') {
      targetScore -= 5
    }

    // Board distribution
    if (input.boardDistribution?.familyId === 'vowel-paring') {
      targetScore -= 5
    }
    if (input.boardDistribution?.familyId === 'no-common-consonants') {
      targetScore -= 10
    }

    // Special tiles
    if (
      input.specialTileReveals.some(reveal => reveal.supplyId === 'regenerating-steady')
    ) {
      targetScore -= 0
    }

    // Bonuses
    input.bonuses.forEach(bonus => {
      targetScore += 10
    })

    // Interval time
    const timeScale = input.settings.intervalTimeSeconds / RACE_CORE_PRESET.intervalTimeSeconds
    if (timeScale > 1) {
      // Account for gold rush easier to reach
      targetScore *= timeScale * 1.4
    } else if (timeScale < 0) {
      targetScore -= timeScale * 0.8
    }

    return {
      aceScore: Math.round((targetScore * 7) / 3),
      targetScore: Math.round(targetScore),
    }
  },
  steps: [
    createBonusStep(() => [
      {
        value: [{ bonusId: 'double-letter', value: 'double' }],
        weight: 1,
      },
      {
        value: [{ bonusId: 'longshot', value: 3 }],
        weight: 1,
      },
      {
        value: [{ bonusId: 'no-repeats', value: 1 }],
        weight: 1,
      },
      {
        value: [{ bonusId: 'vowel-scarcity', value: 1 }],
        weight: 1,
      },
    ]),
    createVowelRatioBandStep(() => [
      {
        value: null,
        weight: 5,
      },
      {
        value: {
          minVowelRatio: 0.2,
          maxVowelRatioExclusive: 0.4,
        },
        weight: 1,
      },
    ]),
    createGridSizeStep(() => [
      { value: '3x3', weight: 1 },
      { value: '4x3', weight: 3 },
      { value: '4x4', weight: 1 },
    ]),
    createMinWordLengthStep(({ bonuses }) => [
      { value: null, weight: 10 },
      { value: 4, weight: 1 },
    ]),
    createIntervalTimeStep(({ rolledState }) => [
      { value: 60, weight: 1 },
      { value: 80, weight: 4 },
      { value: 100, weight: 2 },
    ]),
    createBoardDistributionStep(({ rolledState }) => [
      { value: 'vowel-paring', weight: 3 },
      { value: 'no-common-consonants', weight: 1 },
      { value: 'standard', weight: 3 },
    ]),
    createSpecialTileStep(({ rolledState }) => ({
      options: [
        {
          value: specialTileDefinition({
            interactionId: null,
            kind: 'gold',
            supplyId: 'regenerating-steady',
          }),
          weight: 3,
        },
        {
          value: specialTileDefinition({
            interactionId: null,
            kind: 'purple',
            supplyId: 'regenerating-steady',
          }),
          weight: 1,
        },
        {
          value: specialTileDefinition({
            interactionId: null,
            kind: 'blue',
            supplyId: 'regenerating-slow',
          }),
          weight: 1,
        },
      ],
    })),
  ],
} as const satisfies BoggleChallengeConfigurationDefinition<'basic'>
